import { useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import YouboraTracking from 'providers/player/YouboraTracking';

interface YouboraNavigationHook {
    startTracking: () => (() => void) | null;
}

const TRACK_DEBOUNCE_MS = 600;

export const useYouboraNavigation = (): YouboraNavigationHook => {
    const location = useLocation();
    const isListenerActive = useRef(false);
    const currentPath = useRef<string | null>(null);
    const isInitialized = useRef(false);
    const debounceTimer = useRef<number | null>(null);

    const scheduleTrack = (path: string) => {
        const youboraTracking = YouboraTracking.getInstance();
        if (!youboraTracking.isInitialized) {
            return;
        }

        if (currentPath.current === path) {
            return;
        }

        if (debounceTimer.current !== null) {
            window.clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = window.setTimeout(() => {
            debounceTimer.current = null;
            youboraTracking.trackPageNavigation(path);
            currentPath.current = path;
        }, TRACK_DEBOUNCE_MS);
    };

    useEffect(() => {
        if (!isInitialized.current) {
            return undefined;
        }

        scheduleTrack(location.pathname);

        return () => {
            if (debounceTimer.current !== null) {
                window.clearTimeout(debounceTimer.current);
                debounceTimer.current = null;
            }
        };
    }, [location.pathname]);

    const startTracking = useCallback((): (() => void) | null => {
        const youboraTracking = YouboraTracking.getInstance();

        if (!youboraTracking.isInitialized) {
            return null;
        }

        if (isListenerActive.current) {
            return null;
        }

        scheduleTrack(window.location.pathname);

        isListenerActive.current = true;
        isInitialized.current = true;

        return () => {
            isListenerActive.current = false;
            isInitialized.current = false;
            currentPath.current = null;
            if (debounceTimer.current !== null) {
                window.clearTimeout(debounceTimer.current);
                debounceTimer.current = null;
            }
        };
    }, []);

    return {
        startTracking,
    };
};

export default useYouboraNavigation;
